import type { useTranslation } from "react-i18next";

type TFunc = ReturnType<typeof useTranslation>["t"];

/**
 * Resolves the sentinels the AI service writes in place of text a user must
 * not read.
 *
 * Python has no i18n, so anything the guard replaces travels as a sentinel and
 * is spelled out here, in the reader's language. Every surface that renders
 * AI-written text must call this — an unresolved sentinel on screen is worse
 * than the raw value it replaced.
 */

const TOOL_ERROR = /\[\[ai_tool_error(?::([a-z_]+))?\]\]/g;
// Case-tolerant, and the key is lowercased before the lookup: the Python guard
// matches tool names case-insensitively, so a sentinel can carry the casing the
// model used. A sentinel this side fails to match renders as raw markup.
const TOOL_NAME = /\[\[tool:([a-zA-Z0-9_]+)\]\]/g;

const TOOL_ERROR_CONTEXT_KEYS = new Set([
  "bi_context_lookup",
  "bi_context_update",
  "context_lookup",
  "context_update",
  "email_destinations_lookup",
  "group_context_lookup",
  "partner_context_lookup",
  "partner_search",
  "pre_order_lookup",
  "ai_pre_order_creation",
  "email_linking",
  "pre_order_proposal_cancellation",
  "ai_pre_order_update",
  "product_context_lookup",
  "product_search",
  "transport_history_lookup",
  "transport_pre_order_lookup",
  "employee_lookup",
  "order_creation",
]);

export function resolveAiText(content: string, t: TFunc): string {
  return content
    .replace(TOOL_ERROR, (_match, contextKey: string | undefined) => {
      if (!contextKey || !TOOL_ERROR_CONTEXT_KEYS.has(contextKey)) {
        return t("ai.toolError");
      }
      return t("ai.toolErrorWithContext", {
        context: t(`ai.toolErrorContexts.${contextKey}`),
      });
    })
    .replace(TOOL_NAME, (_match, toolName: string) => {
      const key = `ai.toolNames.${toolName.toLowerCase()}`;
      const label = t(key);
      // A miss echoes the key back. Deliberately NOT translateToolName's
      // fallback: humanizing an unlisted name prints the very thing the guard
      // masked, only prettier ("dispatch_workflows" → "Dispatch workflows").
      // The dictionary will always lag the registry, so the miss must be safe.
      return label === key ? t("ai.internalStep") : label;
    });
}
